import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient.js";
import { getLog, clearLog, copyLog, subscribe } from "./errorlog.js";

export default function UIDebugPanel({ isOwner }) {
  const [open, setOpen] = useState(false);
  const [nBadge, setNBadge] = useState(() => { try { return window.__tpmsNotifNew || 0; } catch { return 0; } });
  const [nPulse, setNPulse] = useState(false);
  const nPrev = React.useRef(0);
  useEffect(() => { const h = (e) => {
    const v = e.detail || 0;
    if (v > nPrev.current) { setNPulse(true); setTimeout(() => setNPulse(false), 3600); }
    nPrev.current = v;
    setNBadge(v);
  }; window.addEventListener("tpms:notif-count", h); return () => window.removeEventListener("tpms:notif-count", h); }, []);
  const [tab, setTab] = useState("errors");
  const [dbSettings, setDbSettings] = useState(null);
  const [cssVars, setCssVars] = useState({});
  const [elements, setElements] = useState({});
  const [log, setLog] = useState([]);
  const [copied, setCopied] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [cloud, setCloud] = useState([]);
  const [cloudErr, setCloudErr] = useState("");

  useEffect(() => {
    const rf = () => setLog([...getLog()]);
    rf();
    return subscribe(rf);
  }, []);

  useEffect(() => {
    if (!open || tab !== "cloud") return;
    (async () => {
      try {
        const { data, error } = await supabase.from("error_logs").select("device, type, msg, extra, at").order("at", { ascending: false }).limit(100);
        if (error) setCloudErr(error.message || String(error));
        else { setCloud(data || []); setCloudErr(""); }
      } catch (e) { setCloudErr(String(e && e.message || e)); }
    })();
  }, [open, tab, refresh]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await supabase.from("platform_ui_settings").select("settings, updated_at").eq("is_active", true).limit(1).maybeSingle();
        setDbSettings(data);
      } catch (e) { setDbSettings({ error: String(e && e.message || e) }); }
      const r = document.documentElement;
      const cs = getComputedStyle(r);
      const vars = ["--paper", "--bg", "--surface", "--accent", "--accent-hover", "--icon-color", "--text", "--grid-color", "--grid-strong", "--line"];
      const applied = {};
      vars.forEach(v => { applied[v] = r.style.getPropertyValue(v) || cs.getPropertyValue(v) || "(default)"; });
      setCssVars(applied);
      setElements({
        "ui-dynamic-grid": !!document.getElementById("ui-dynamic-grid"),
        "ui-logo-style": !!document.getElementById("ui-logo-style"),
        "ui-type-style": !!document.getElementById("ui-type-style"),
        "ui-hover-style": !!document.getElementById("ui-hover-style"),
        "theme": r.getAttribute("data-theme"),
        "__applyUISettings": typeof window.__applyUISettings
      });
    })();
  }, [open, refresh]);

  // دمج المتكرر المتتالي بعداد
  const rows = [];
  for (const e of log) {
    const last = rows[rows.length - 1];
    if (last && last.type === e.type && last.msg === e.msg) { last.n += 1; last.at = last.at; }
    else rows.push({ ...e, n: 1 });
  }

  const doCopy = () => { const t = copyLog(); try { navigator.clipboard.writeText(t); } catch {} setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const doClear = () => { clearLog(); setLog([]); };
  const reapply = async () => {
    try {
      const { data } = await supabase.from("platform_ui_settings").select("settings").eq("is_active", true).limit(1).maybeSingle();
      if (data && data.settings && window.__applyUISettings) { window.__applyUISettings(data.settings); setRefresh(r => r + 1); }
    } catch (e) { console.error("Reapply failed:", e); }
  };

  if (!isOwner) return null;
  const errCount = rows.filter(r => r.type !== "info").reduce((a, r) => a + r.n, 0);

  return (
    <>
      <style>{`@keyframes tpmsNotifPulse { 0%, 100% { transform: scale(1); box-shadow: 0 4px 14px rgba(214,58,122,.45); } 50% { transform: scale(1.18); box-shadow: 0 4px 14px rgba(214,58,122,.45), 0 0 0 9px rgba(214,58,122,.18); } }`}</style>
      <button onClick={() => { try { window.dispatchEvent(new CustomEvent("tpms:open-notifs")); } catch {} }} style={{ position: "fixed", bottom: 68, insetInlineStart: 16, zIndex: 9950, width: 44, height: 44, borderRadius: "50%", border: "none", background: "#d63a7a", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(214,58,122,.45)", animation: nPulse ? "tpmsNotifPulse 1.2s ease-in-out 3" : "none" }}>{nBadge > 9 ? "9+" : nBadge}</button>
      <button onClick={() => setOpen(o => !o)} style={{ position: "fixed", bottom: 16, insetInlineStart: 16, zIndex: 9950, width: 44, height: 44, borderRadius: "50%", border: "none", background: "#d63a7a", color: "#fff", fontSize: 19, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(214,58,122,.45)" }}>{open ? "×" : "?"}</button>
      {open && (
        <div dir="rtl" style={{ position: "fixed", bottom: 70, insetInlineStart: 10, zIndex: 9949, width: "min(420px, 94vw)", maxHeight: "70vh", background: "color-mix(in srgb, var(--surface) var(--modal-alpha, 88%), transparent)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", color: "var(--text)", borderRadius: 0, border: "none", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "ui-monospace, monospace", boxShadow: "4px 4px 0 color-mix(in srgb, var(--accent) 30%, transparent)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
            <span style={{ fontWeight: 800, fontSize: 12, color: "var(--accent)" }}>UI Debug</span>
            <button onClick={reapply} style={{ marginInlineStart: "auto", padding: "5px 10px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: 0, cursor: "pointer", fontSize: 10.5, fontFamily: "inherit" }}>Re-apply</button>
            <button onClick={() => setRefresh(r => r + 1)} style={{ padding: "5px 10px", background: "transparent", color: "var(--text)", border: "1px solid var(--line)", borderRadius: 0, cursor: "pointer", fontSize: 10.5, fontFamily: "inherit" }}>Refresh</button>
          </div>
          <div style={{ display: "flex", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
            {["errors", "cloud", "elements", "css", "settings"].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "8px 4px", background: tab === t ? "color-mix(in srgb, var(--accent) 10%, transparent)" : "transparent", color: tab === t ? "var(--accent)" : "var(--faint)", border: "none", borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent", cursor: "pointer", fontSize: 10.5, fontWeight: 700, fontFamily: "inherit", textTransform: "uppercase" }}>
                {t}{t === "errors" && errCount > 0 && <span style={{ marginInlineStart: 4, background: "#ef4444", color: "#fff", padding: "1px 6px", borderRadius: 8, fontSize: 9 }}>{errCount}</span>}
              </button>
            ))}
          </div>
          {tab === "errors" && (
            <div style={{ display: "flex", gap: 8, padding: "8px 12px", borderBottom: "1px solid #1e293b", flexShrink: 0 }}>
              <button onClick={doCopy} style={{ flex: 1, minHeight: 38, background: "var(--accent)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 800, fontFamily: "inherit" }}>{copied ? "نُسخ ✓" : "نسخ الكل"}</button>
              <button onClick={doClear} style={{ flex: 1, minHeight: 38, background: "transparent", color: "var(--text)", border: "1px solid var(--line)", borderRadius: 0, cursor: "pointer", fontSize: 12, fontWeight: 800, fontFamily: "inherit" }}>مسح السجل</button>
            </div>
          )}
          <div style={{ overflowY: "auto", padding: 10, fontSize: 10.5, lineHeight: 1.7 }}>
            {tab === "errors" && (rows.length === 0
              ? <div style={{ color: "var(--faint)", textAlign: "center", padding: 20 }}>لا أخطاء مسجلة</div>
              : rows.map((e, i) => (
                <div key={i} style={{ marginBottom: 6, padding: "6px 9px", borderRadius: 6, background: "var(--surfaceHi)", borderInlineStart: e.type === "info" ? "3px solid var(--line)" : "3px solid var(--accent)", direction: "ltr", textAlign: "left", wordBreak: "break-word" }}>
                  <span style={{ color: "var(--faint)" }}>[{e.at}]</span> <span style={{ color: e.type === "info" ? "var(--faint)" : "var(--accent)" }}>{e.type}</span>{e.n > 1 && <span style={{ background: "#ef4444", color: "#fff", padding: "0 6px", borderRadius: 8, fontSize: 9, marginInline: 5 }}>×{e.n}</span>}<br />{e.msg}{e.extra ? " @ " + e.extra : ""}
                </div>
              )))}
            {tab === "cloud" && (cloudErr
              ? <div style={{ color: "#fca5a5", direction: "ltr", textAlign: "left" }}>CLOUD error: {cloudErr}</div>
              : cloud.length === 0
                ? <div style={{ color: "#64748b", textAlign: "center", padding: 20 }}>لا أخطاء سحابية بعد</div>
                : cloud.map((e, i) => (
                  <div key={i} style={{ marginBottom: 6, padding: "6px 9px", borderRadius: 6, background: "var(--surfaceHi)", borderInlineStart: "3px solid var(--accent)", direction: "ltr", textAlign: "left", wordBreak: "break-word" }}>
                    <span style={{ color: "#64748b" }}>[{new Date(e.at).toLocaleTimeString("ar-KW")}]</span> <span style={{ color: "#7dd3fc" }}>{e.device}</span> <span style={{ color: "#fca5a5" }}>{e.type}</span><br />{e.msg}{e.extra ? " @ " + e.extra : ""}
                  </div>
                )))}
            {tab === "elements" && Object.entries(elements).map(([k, v]) => <div key={k} style={{ direction: "ltr", textAlign: "left" }}><span style={{ color: "#94a3b8" }}>{k}:</span> <span style={{ color: v === true ? "#4ade80" : v === false ? "#ef4444" : "#e2e8f0" }}>{String(v)}</span></div>)}
            {tab === "css" && Object.entries(cssVars).map(([k, v]) => <div key={k} style={{ direction: "ltr", textAlign: "left", display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 14, borderRadius: 3, background: v, border: "1px solid #334155", flexShrink: 0 }} /><span style={{ color: "#94a3b8" }}>{k}:</span> {v}</div>)}
            {tab === "settings" && <pre style={{ direction: "ltr", textAlign: "left", whiteSpace: "pre-wrap", margin: 0 }}>{JSON.stringify(dbSettings, null, 1)}</pre>}
          </div>
        </div>
      )}
    </>
  );
}
